import type { HeadFC, PageProps } from 'gatsby';
import { graphql } from 'gatsby';
import type { FC } from 'react';

import type {
  ProductDetail,
  SimilarProducts as SimilarProductsType,
} from '../../../@types/gql/product-detail';

import ProductDetailAbout from '../../../components/product/detail/about';
import ProductDetailInfo from '../../../components/product/detail/info';
import SimilarProducts from '../../../components/product/detail/similar-products';
import Breadcrumb from '../../../components/shared/breadcrum';
import SEOComponent from '../../../components/shared/seo';

export const query = graphql`
  query ProductDetailQuery($product: String!, $subCategory: String) {
    strapiProduct(Slug: { eq: $product }) {
      seo {
        ...SEOFragment
      }
      id: strapiId
      Title
      youtubePlaylist
      Thumbnail {
        url
      }
      Brochure {
        url
        name
      }
      subCategory: Category {
        Title
        Slug
        Category {
          Title
          Slug
        }
      }
      Images {
        id
        url
      }
      Description
      Specifications {
        id
        TableData {
          id
          Name
        }
        TableHeading
      }
      ComponentsAndAccessories {
        id
        Heading
        Content {
          id
          Title
          List {
            id
            Name
          }
        }
      }
    }
    similarProducts: allStrapiProduct(
      filter: {
        Slug: { ne: $product }
        Category: { Slug: { eq: $subCategory } }
      }
      limit: 10
    ) {
      nodes {
        id
        Title
        Thumbnail {
          url
        }
        Slug
        subCategory: Category {
          Slug
          Category {
            Slug
          }
        }
        Description
      }
    }
  }
`;

const ProductDetailTemplate: FC<
  PageProps<
    { strapiProduct: ProductDetail; similarProducts: SimilarProductsType },
    {
      product: string;
      category: string;
      subCategory: string;
    }
  >
> = ({ data, pageContext }) => {
  const { strapiProduct, similarProducts } = data;

  return (
    <div className="pt-5">
      <Breadcrumb
        items={[
          {
            label: strapiProduct.subCategory.Category.Title.toUpperCase(),
            link: `/${pageContext.category}/${pageContext.subCategory}`,
          },
          {
            label: strapiProduct.subCategory.Title.toUpperCase(),
            link: `/${pageContext.category}/${pageContext.subCategory}`,
          },
          {
            label: strapiProduct.Title.toUpperCase(),
            link: `/${pageContext.category}/${pageContext.subCategory}/${pageContext.product}`,
          },
        ]}
      />
      <ProductDetailAbout
        brochure={strapiProduct.Brochure}
        description={strapiProduct.Description}
        id={strapiProduct.id}
        images={
          strapiProduct.Images?.length
            ? [...strapiProduct.Images.map(({ url }) => url)]
            : [strapiProduct.Thumbnail.url]
        }
        title={strapiProduct.Title}
        youtubePlaylist={strapiProduct.youtubePlaylist}
      />
      <ProductDetailInfo
        components={strapiProduct.ComponentsAndAccessories || []}
        specification={{
          description: strapiProduct.SpecificationDescription,
          list: strapiProduct.Specifications || [],
        }}
      />

      {similarProducts.nodes?.length ? (
        <SimilarProducts similarProducts={similarProducts} />
      ) : null}

      {/* {similarProducts.nodes?.length ? (
        <SimilarProducts similarProducts={similarProducts} />
      ) : null} */}
    </div>
  );
};

export default ProductDetailTemplate;

export const Head: HeadFC<{ strapiProduct: ProductDetail }> = ({ data }) => (
  <SEOComponent seo={data.strapiProduct.seo}  />
);
